import React, { useState, useEffect } from 'react';

export default function HttpApiModal({ open, onClose, node, onSave }) {
  const [formData, setFormData] = useState({
    apiList: [],
    selectedApi: '',
    apiName: '',
    method: 'POST',
    url: '',
    headers: {},
    body: '',
    delay: 0
  });
  const [newHeader, setNewHeader] = useState({ key: '', value: '' });

  useEffect(() => {
    if (open && node) {
      setFormData({
        apiList: node.data.apiList || [],
        selectedApi: node.data.selectedApi || '',
        apiName: node.data.apiName || '',
        method: node.data.method || 'POST',
        url: node.data.url || '',
        headers: node.data.headers || {},
        body: node.data.body || '',
        delay: node.data.delay || 0
      });
    }
  }, [open, node]);

  const handleSave = () => {
    if (onSave) {
      onSave(node.id, formData);
    }
    onClose();
  };

  const createApi = () => {
    if (formData.apiName.trim() && formData.url.trim()) {
      const newApi = {
        name: formData.apiName,
        method: formData.method,
        url: formData.url,
        headers: formData.headers,
        body: formData.body
      };
      setFormData(prev => ({
        ...prev,
        apiList: [...prev.apiList, newApi],
        selectedApi: newApi.name,
        apiName: '',
        url: '',
        headers: {},
        body: ''
      }));
    }
  };

  const editApi = () => {
    const selectedApiData = formData.apiList.find(api => api.name === formData.selectedApi);
    if (selectedApiData) {
      setFormData(prev => ({
        ...prev,
        apiName: selectedApiData.name,
        method: selectedApiData.method,
        url: selectedApiData.url,
        headers: selectedApiData.headers,
        body: selectedApiData.body
      }));
    }
  };

  const addHeader = () => {
    if (newHeader.key.trim() && newHeader.value.trim()) {
      setFormData(prev => ({
        ...prev,
        headers: {
          ...prev.headers,
          [newHeader.key]: newHeader.value
        }
      }));
      setNewHeader({ key: '', value: '' });
    }
  };

  const removeHeader = (key) => {
    const newHeaders = { ...formData.headers };
    delete newHeaders[key];
    setFormData(prev => ({
      ...prev,
      headers: newHeaders
    }));
  };

  const refreshApis = () => {
    // This would typically reload APIs from backend
    console.log('Refreshing APIs...');
  };

  if (!open) return null;

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  };

  const contentStyle = {
    background: '#ffffff',
    borderRadius: '8px',
    padding: '24px',
    minWidth: '700px',
    maxWidth: '800px',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
  };

  const headerStyle = {
    background: '#1e3a8a',
    color: '#ffffff',
    padding: '16px 24px',
    margin: '-24px -24px 24px -24px',
    borderRadius: '8px 8px 0 0',
    fontWeight: 'bold',
    fontSize: '18px'
  };

  const inputStyle = {
    width: '100%',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '14px',
    marginBottom: '12px',
    outline: 'none'
  };

  const selectStyle = {
    ...inputStyle,
    backgroundColor: '#ffffff'
  };

  const buttonStyle = {
    background: '#007bff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    marginRight: '8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  };

  const secondaryButtonStyle = {
    background: '#6c757d',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    marginRight: '8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  };

  const orangeButtonStyle = {
    ...buttonStyle,
    background: '#fd7e14'
  };

  const greenButtonStyle = {
    ...buttonStyle,
    background: '#28a745'
  };

  const lightBlueButtonStyle = {
    ...buttonStyle,
    background: '#17a2b8'
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '100px',
    resize: 'vertical',
    fontFamily: 'inherit'
  };

  const dividerStyle = {
    border: 'none',
    borderTop: '2px dashed #d1d5db',
    margin: '24px 0'
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          Configure HTTP API
        </div>

        {/* HTTP API List Section */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            HTTP API List
          </label>
          
          {/* API Dropdown */}
          <select
            value={formData.selectedApi}
            onChange={e => setFormData(prev => ({ ...prev, selectedApi: e.target.value }))}
            style={selectStyle}
          >
            <option value="">Select an API...</option>
            {formData.apiList.map((api, index) => (
              <option key={index} value={api.name}>
                {api.name} : {api.method}
              </option>
            ))}
          </select>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button onClick={createApi} style={buttonStyle}>
              ➕ Create
            </button>
            <button onClick={editApi} style={orangeButtonStyle}>
              ✏️ Edit
            </button>
            <button style={greenButtonStyle}>
              👁️ Report
            </button>
            <button onClick={refreshApis} style={lightBlueButtonStyle}>
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Divider */}
        <hr style={dividerStyle} />

        {/* API Configuration Form */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#495057' }}>
                API Name
              </label>
              <input
                type="text"
                value={formData.apiName}
                onChange={e => setFormData(prev => ({ ...prev, apiName: e.target.value }))}
                placeholder="Enter API name..."
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#495057' }}>
                Method
              </label>
              <select
                value={formData.method}
                onChange={e => setFormData(prev => ({ ...prev, method: e.target.value }))}
                style={selectStyle}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#495057' }}>
              URL
            </label>
            <input
              type="url"
              value={formData.url}
              onChange={e => setFormData(prev => ({ ...prev, url: e.target.value }))}
              placeholder="https://api.example.com/endpoint"
              style={inputStyle}
            />
          </div>

          {/* Headers Section */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
              Headers
            </label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="text"
                value={newHeader.key}
                onChange={e => setNewHeader(prev => ({ ...prev, key: e.target.value }))}
                placeholder="Header key"
                style={{ ...inputStyle, marginBottom: 0 }}
              />
              <input
                type="text"
                value={newHeader.value}
                onChange={e => setNewHeader(prev => ({ ...prev, value: e.target.value }))}
                placeholder="Header value"
                style={{ ...inputStyle, marginBottom: 0 }}
              />
              <button onClick={addHeader} style={{ ...buttonStyle, marginBottom: 0 }}>
                Add
              </button>
            </div>
            {Object.keys(formData.headers).length > 0 && (
              <div style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '8px' }}>
                {Object.entries(formData.headers).map(([key, value]) => (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '12px' }}><strong>{key}:</strong> {value}</span>
                    <button
                      onClick={() => removeHeader(key)}
                      style={{ background: '#dc3545', color: '#ffffff', border: 'none', borderRadius: '2px', padding: '2px 6px', fontSize: '10px', cursor: 'pointer' }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Request Body */}
          {formData.method !== 'GET' && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#495057' }}>
                Request Body
              </label>
              <textarea
                value={formData.body}
                onChange={e => setFormData(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Enter request body (JSON format)"
                style={textareaStyle}
              />
            </div>
          )}

          {/* Delay Setting */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
              Delay in reply - {formData.delay} sec
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input
                type="range"
                min="0"
                max="30"
                value={formData.delay}
                onChange={e => setFormData(prev => ({ ...prev, delay: parseInt(e.target.value) }))}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: '14px', color: '#6c757d', minWidth: '50px' }}>
                {formData.delay} sec
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button onClick={handleSave} style={buttonStyle}>
            💾 Save
          </button>
          <button onClick={onClose} style={secondaryButtonStyle}>
            ✕ Close
          </button>
        </div>
      </div>
    </div>
  );
} 